<template>
	<div class="relative w-full space-y-2">
		<div class="mb-1 flex items-center">
			<div class="w-full space-y-2">
				<div class="flex w-full flex-col items-center">
					<div class="mb-2 flex items-center gap-2 px-1 text-sm text-gray-600 dark:text-gray-300">
						<span class="fa-light fa-filter"></span>
						<span>Filters</span>
					</div>

					<div class="flex max-w-screen-2xl items-center justify-center gap-3">
						<div class="inline-flex flex-col items-center gap-3 sm:flex-row">
							<div
								class="inline-flex rounded-lg border bg-white p-2 shadow-xl backdrop-blur-sm dark:border-secondary dark:bg-main"
							>
								<div class="flex flex-wrap justify-center gap-2">
									<template v-for="(filter, index) in visibleFilters" :key="index">
										<Popover v-if="!isMobile" v-model:open="filter.isOpen" class="relative">
											<PopoverTrigger
												v-if="
													filter.needsTenants
														? $global.hasUserRole(userRoles, 'reseller') ||
															$global.hasUserRole(userRoles, 'admin') ||
															tenants.length > 1
														: filter.needsAccounts
															? ($global.hasUserRole(userRoles, 'tenant') &&
																	accounts.length > 1) ||
																($global.hasUserRole(userRoles, 'reseller') &&
																	accounts.length > 0) ||
																$global.hasUserRole(userRoles, 'admin')
															: filter.name === 'carrier'
																? !$global.hasUserRole(userRoles, 'account')
																: true
												"
												class="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600 dark:hover:bg-gray-700"
												:class="{
													'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20':
														filter.isOpen
												}"
											>
												<span class="fa-light" :class="getFilterIcon(filter.name)"></span>
												<span>{{ filter.label }}</span>
												<span
													v-if="filter.hasValue"
													class="flex h-2 w-2 rounded-full bg-blue-600"
												></span>
											</PopoverTrigger>

											<PopoverContent
												class="z-50 min-w-[320px] rounded-lg border bg-white p-0 shadow-lg dark:border-gray-700 dark:bg-[#2B303B] sm:w-[640px]"
												:class="{ '!md:w-[900px]': filter.name === 'dates' }"
											>
												<div
													class="flex h-full max-h-[calc(100vh-160px)] flex-col overflow-y-auto"
												>
													<div
														class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700"
													>
														<h3 class="text-lg font-semibold text-gray-900 dark:text-white">
															{{ getFilterTitle(filter.name) }}
														</h3>
														<button
															class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
															@click="closePopover(filter)"
														>
															<span class="fa-light fa-xmark text-xl"></span>
														</button>
													</div>

													<template v-if="filter.name === 'dates'">
														<div class="p-4">
															<div
																class="no-scrollbar mb-4 overflow-x-auto whitespace-nowrap"
															>
																<button
																	class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
																	@click="setPresetDate('24h')"
																>
																	Last 24 hours
																</button>
																<button
																	class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
																	@click="setPresetDate('7d')"
																>
																	Last 7 days
																</button>
																<button
																	class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
																	@click="setPresetDate('30d')"
																>
																	Last 30 days
																</button>
																<button
																	class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
																	@click="setPresetDate('thisMonth')"
																>
																	This Month
																</button>
																<button
																	class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
																	@click="setPresetDate('lastMonth')"
																>
																	Last Month
																</button>
															</div>
															<vue-date-picker
																v-model="dates"
																range
																inline
																:multi-calendars="calendarCount"
																:enable-time-picker="false"
																format="dd/MM/yyyy"
																class="date-picker-custom w-full"
																auto-apply
																:dark="true"
																:max-range="maxDateRange"
																@update:model-value="handleDateChange"
															/>
														</div>
													</template>
													<template v-else>
														<div class="p-4">
															<template v-if="filter.name === 'tenant'">
																<div>
																	<input
																		v-model="tenantSearch"
																		type="text"
																		placeholder="Search tenants..."
																		class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
																	/>
																	<div
																		class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
																	>
																		<div
																			v-if="filteredTenants.length === 0"
																			class="p-4 text-center text-gray-500 dark:text-gray-400"
																		>
																			No matching tenants found
																		</div>
																		<div
																			class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					!selectedTenant
																			}"
																			@click="selectTenant(null)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-users-slash text-base"
																				></span>
																				<span>All Tenants</span>
																			</div>
																		</div>
																		<div
																			v-for="tenant in filteredTenants"
																			:key="tenant.id"
																			class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					selectedTenant?.id === tenant.id
																			}"
																			@click="selectTenant(tenant)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-building text-base"
																				></span>
																				<span>{{ tenant.companyName }}</span>
																			</div>
																		</div>
																	</div>
																</div>
															</template>

															<template v-else-if="filter.name === 'carrier'">
																<div>
																	<input
																		v-model="carrierSearch"
																		type="text"
																		placeholder="Search carriers..."
																		class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
																	/>
																	<div
																		class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
																	>
																		<div
																			v-if="filteredCarriers.length === 0"
																			class="p-4 text-center text-gray-500 dark:text-gray-400"
																		>
																			No matching carriers found
																		</div>
																		<div
																			class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					!selectedCarrier
																			}"
																			@click="selectCarrier(null)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-satellite-dish text-base"
																				></span>
																				<span>All Carriers</span>
																			</div>
																		</div>
																		<div
																			v-for="carrier in filteredCarriers"
																			:key="carrier.id"
																			class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					selectedCarrier?.id === carrier.id
																			}"
																			@click="selectCarrier(carrier)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-satellite-dish text-base"
																				></span>
																				<span>{{ carrier.name }}</span>
																			</div>
																		</div>
																	</div>
																</div>
															</template>

															<template v-else-if="filter.name === 'account'">
																<div>
																	<input
																		v-model="accountSearch"
																		type="text"
																		placeholder="Search accounts..."
																		class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
																	/>
																	<div
																		class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
																	>
																		<div
																			v-if="
																				filteredAccounts.length === 0 &&
																				!selectedTenant
																			"
																			class="p-4 text-center text-gray-500 dark:text-gray-400"
																		>
																			Please select a tenant first
																		</div>
																		<div
																			v-else-if="filteredAccounts.length === 0"
																			class="p-4 text-center text-gray-500 dark:text-gray-400"
																		>
																			{{
																				accountSearch
																					? 'No matching accounts found'
																					: 'No accounts available'
																			}}
																		</div>
																		<div
																			v-if="selectedTenant"
																			class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					!selectedAccount
																			}"
																			@click="selectAccount(null)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-users-slash text-base"
																				></span>
																				<span>All Accounts</span>
																			</div>
																		</div>
																		<div
																			v-for="account in filteredAccounts"
																			:key="account.id"
																			class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
																			:class="{
																				'bg-blue-50 dark:bg-blue-900/20':
																					selectedAccount?.id === account.id
																			}"
																			@click="selectAccount(account)"
																		>
																			<div class="flex items-center gap-2">
																				<span
																					class="fa-light fa-user text-base"
																				></span>
																				<span>{{ account.name }}</span>
																			</div>
																		</div>
																	</div>
																</div>
															</template>

															<template v-if="filter.name === 'duration'">
																<div class="space-y-6">
																	<div>
																		<h4
																			class="mb-2 font-medium text-gray-900 dark:text-white"
																		>
																			Call Duration (seconds)
																		</h4>
																		<div class="flex gap-4">
																			<div class="flex-1">
																				<label
																					class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																					>Min</label
																				>
																				<input
																					v-model="durationMin"
																					type="number"
																					min="0"
																					class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
																					@change="validateAndUpdateDuration"
																				/>
																			</div>
																			<div class="flex-1">
																				<label
																					class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																					>Max</label
																				>
																				<input
																					v-model="durationMax"
																					type="number"
																					min="0"
																					class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
																					@change="validateAndUpdateDuration"
																				/>
																			</div>
																		</div>
																	</div>
																	<div>
																		<h4
																			class="mb-2 font-medium text-gray-900 dark:text-white"
																		>
																			Billable Duration (seconds)
																		</h4>
																		<div class="flex gap-4">
																			<div class="flex-1">
																				<label
																					class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																					>Min</label
																				>
																				<input
																					v-model="billableDurationMin"
																					type="number"
																					min="0"
																					class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
																					@change="validateAndUpdateDuration"
																				/>
																			</div>
																			<div class="flex-1">
																				<label
																					class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																					>Max</label
																				>
																				<input
																					v-model="billableDurationMax"
																					type="number"
																					min="0"
																					class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
																					@change="validateAndUpdateDuration"
																				/>
																			</div>
																		</div>
																	</div>
																</div>
															</template>

															<template v-if="filter.name === 'zone'">
																<div>
																	<input
																		v-model="zoneSearch"
																		type="text"
																		placeholder="Search zones..."
																		class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
																	/>
																	<div
																		class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
																	>
																		<div
																			v-for="zone in filteredZones"
																			:key="zone"
																			class="flex cursor-pointer items-center p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
																			@click="toggleZone(zone)"
																		>
																			<span
																				class="fa-light mr-2"
																				:class="
																					selectedZones.includes(zone)
																						? 'fa-square-check'
																						: 'fa-square'
																				"
																			></span>
																			{{ zone }}
																		</div>
																	</div>
																</div>
															</template>

															<template v-if="filter.name === 'call'">
																<div class="space-y-4">
																	<div>
																		<label
																			class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																			>Call From</label
																		>
																		<input
																			v-model="callFrom"
																			type="text"
																			class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
																			:disabled="!!callAnywhere"
																			@change="updateFilterSection('call')"
																		/>
																	</div>
																	<div>
																		<label
																			class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																			>Call To</label
																		>
																		<input
																			v-model="callTo"
																			type="text"
																			class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
																			:disabled="!!callAnywhere"
																			@change="updateFilterSection('call')"
																		/>
																	</div>
																	<div>
																		<label
																			class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																			>Call Anywhere</label
																		>
																		<input
																			v-model="callAnywhere"
																			type="text"
																			class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
																			:disabled="!!(callFrom || callTo)"
																			@change="handleCallAnywhereChange"
																		/>
																	</div>
																	<div class="space-y-2">
																		<label
																			class="mb-1 block text-sm text-gray-600 dark:text-gray-400"
																			>Filters</label
																		>
																		<div class="flex flex-col space-y-2">
																			<div class="flex items-center">
																				<input
																					id="withRecording"
																					v-model="withRecording"
																					type="checkbox"
																					class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
																					@change="
																						updateFilterSection('call')
																					"
																				/>
																				<label
																					for="withRecording"
																					class="ml-2 text-sm text-gray-700 dark:text-gray-300"
																					>With Recording</label
																				>
																			</div>
																			<div class="flex items-center">
																				<input
																					id="withTranscription"
																					v-model="withTranscription"
																					type="checkbox"
																					class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
																					@change="
																						updateFilterSection('call')
																					"
																				/>
																				<label
																					for="withTranscription"
																					class="ml-2 text-sm text-gray-700 dark:text-gray-300"
																					>With Transcription</label
																				>
																			</div>
																			<div class="flex items-center">
																				<input
																					id="withVoicemail"
																					v-model="withVoicemail"
																					type="checkbox"
																					class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
																					@change="
																						updateFilterSection('call')
																					"
																				/>
																				<label
																					for="withVoicemail"
																					class="ml-2 text-sm text-gray-700 dark:text-gray-300"
																					>With Voicemail</label
																				>
																			</div>
																		</div>
																	</div>
																	<div>
																		<label
																			class="mb-2 block text-sm text-gray-600 dark:text-gray-400"
																			>Hangup Codes</label
																		>
																		<div class="max-h-[300px] overflow-y-auto">
																			<div class="space-y-4">
																				<div
																					v-for="(
																						codes, group
																					) in statusCodes"
																					:key="group"
																					class="space-y-2"
																				>
																					<div
																						v-if="group !== 'Common'"
																						class="font-medium text-gray-700 dark:text-gray-300"
																					>
																						{{ group }}
																					</div>
																					<div class="flex flex-wrap gap-1.5">
																						<button
																							v-for="code in codes"
																							:key="code.code"
																							class="flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors"
																							:class="{
																								'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-300':
																									selectedHangupCodes.some(
																										(h) =>
																											h.code ===
																											code.code
																									),
																								'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700':
																									!selectedHangupCodes.some(
																										(h) =>
																											h.code ===
																											code.code
																									)
																							}"
																							:title="code.description"
																							@click="
																								toggleHangupCode(
																									code.code
																								)
																							"
																						>
																							{{ code.code }}
																							<span
																								class="max-w-[120px] truncate text-[10px] text-gray-500 dark:text-gray-400"
																							>
																								{{ code.description }}
																							</span>
																						</button>
																					</div>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</template>
														</div>
													</template>
													<div
														class="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700"
													>
														<button
															class="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
															@click="clearCurrentFilter(filter.name)"
														>
															Clear
														</button>
														<button
															class="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
															@click="applyFilters(filter)"
														>
															Apply
														</button>
													</div>
												</div>
											</PopoverContent>
										</Popover>

										<button
											v-else-if="
												filter.needsTenants
													? $global.hasUserRole(userRoles, 'reseller') ||
														$global.hasUserRole(userRoles, 'admin') ||
														tenants.length > 1
													: filter.needsAccounts
														? ($global.hasUserRole(userRoles, 'tenant') &&
																accounts.length > 1) ||
															($global.hasUserRole(userRoles, 'reseller') &&
																accounts.length > 0) ||
															$global.hasUserRole(userRoles, 'admin')
														: filter.name === 'carrier'
															? !$global.hasUserRole(userRoles, 'account')
															: true
											"
											class="flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm shadow-sm transition-all hover:border-gray-300 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:border-gray-600 dark:hover:bg-gray-700"
											:class="{
												'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/20':
													filter.isOpen
											}"
											@click="openMobileFilter(filter)"
										>
											<span class="fa-light" :class="getFilterIcon(filter.name)"></span>
											<span>{{ filter.label }}</span>
											<span
												v-if="filter.hasValue"
												class="flex h-2 w-2 rounded-full bg-blue-600"
											></span>
										</button>
									</template>
								</div>
							</div>

							<div class="flex gap-2">
								<button
									class="flex shrink-0 items-center justify-center gap-2 rounded-md bg-blue-600 px-6 py-2 text-sm text-white shadow-xl transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
									:disabled="loading"
									@click="applyFilters(null, true)"
								>
									<span v-if="loading" class="fa-light fa-spinner-third fa-spin"></span>
									<span v-else class="fa-light fa-magnifying-glass text-base"></span>
									<span class="inline sm:hidden xl:inline">{{ searchButtonText }}</span>
								</button>
								<button
									class="flex shrink-0 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-6 py-2 text-sm text-gray-600 shadow-xl transition-colors hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:bg-gray-700 dark:hover:text-white"
									@click="clearFilters"
								>
									<span class="fa-light fa-rotate-left text-base"></span>
									<span class="inline sm:hidden xl:inline">Reset All</span>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div v-if="isMobile && activeFilter" class="fixed inset-0 z-50 flex flex-col bg-white dark:bg-[#2B303B]">
			<div class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
				<h3 class="text-lg font-semibold text-gray-900 dark:text-white">
					{{ getFilterTitle(activeFilter.name) }}
				</h3>
				<button
					class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
					@click="closeMobileFilter"
				>
					<span class="fa-light fa-xmark text-xl"></span>
				</button>
			</div>

			<div class="flex-1 overflow-y-auto p-4">
				<template v-if="activeFilter.name === 'dates'">
					<div class="no-scrollbar mb-4 overflow-x-auto whitespace-nowrap">
						<button
							class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
							@click="setPresetDate('24h')"
						>
							Last 24 hours
						</button>
						<button
							class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
							@click="setPresetDate('7d')"
						>
							Last 7 days
						</button>
						<button
							class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
							@click="setPresetDate('30d')"
						>
							Last 30 days
						</button>
						<button
							class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
							@click="setPresetDate('thisMonth')"
						>
							This Month
						</button>
						<button
							class="mr-2 rounded bg-gray-200 px-3 py-1 text-xs dark:bg-gray-700 dark:text-white"
							@click="setPresetDate('lastMonth')"
						>
							Last Month
						</button>
					</div>
					<vue-date-picker
						v-model="dates"
						range
						inline
						:multi-calendars="calendarCount"
						:enable-time-picker="false"
						format="dd/MM/yyyy"
						class="date-picker-custom w-full"
						auto-apply
						:dark="true"
						:max-range="maxDateRange"
						@update:model-value="handleDateChange"
					/>
				</template>

				<template v-else>
					<template v-if="activeFilter.name === 'tenant'">
						<div>
							<input
								v-model="tenantSearch"
								type="text"
								placeholder="Search tenants..."
								class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
							/>
							<div
								class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
							>
								<div
									v-if="filteredTenants.length === 0"
									class="p-4 text-center text-gray-500 dark:text-gray-400"
								>
									No matching tenants found
								</div>
								<div
									class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': !selectedTenant }"
									@click="selectTenant(null)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-users-slash text-base"></span>
										<span>All Tenants</span>
									</div>
								</div>
								<div
									v-for="tenant in filteredTenants"
									:key="tenant.id"
									class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': selectedTenant?.id === tenant.id }"
									@click="selectTenant(tenant)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-building text-base"></span>
										<span>{{ tenant.companyName }}</span>
									</div>
								</div>
							</div>
						</div>
					</template>

					<template v-else-if="activeFilter.name === 'carrier'">
						<div>
							<input
								v-model="carrierSearch"
								type="text"
								placeholder="Search carriers..."
								class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
							/>
							<div
								class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
							>
								<div
									v-if="filteredCarriers.length === 0"
									class="p-4 text-center text-gray-500 dark:text-gray-400"
								>
									No matching carriers found
								</div>
								<div
									class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': !selectedCarrier }"
									@click="selectCarrier(null)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-satellite-dish text-base"></span>
										<span>All Carriers</span>
									</div>
								</div>
								<div
									v-for="carrier in filteredCarriers"
									:key="carrier.id"
									class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': selectedCarrier?.id === carrier.id }"
									@click="selectCarrier(carrier)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-satellite-dish text-base"></span>
										<span>{{ carrier.name }}</span>
									</div>
								</div>
							</div>
						</div>
					</template>

					<template v-else-if="activeFilter.name === 'account'">
						<div>
							<input
								v-model="accountSearch"
								type="text"
								placeholder="Search accounts..."
								class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
							/>
							<div
								class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
							>
								<div
									v-if="filteredAccounts.length === 0 && !selectedTenant"
									class="p-4 text-center text-gray-500 dark:text-gray-400"
								>
									Please select a tenant first
								</div>
								<div
									v-if="filteredAccounts.length === 0"
									class="p-4 text-center text-gray-500 dark:text-gray-400"
								>
									{{ accountSearch ? 'No matching accounts found' : 'No accounts available' }}
								</div>
								<div
									v-if="selectedTenant"
									class="cursor-pointer border-b border-gray-200 bg-gray-50 p-2 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': !selectedAccount }"
									@click="selectAccount(null)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-users-slash text-base"></span>
										<span>All Accounts</span>
									</div>
								</div>
								<div
									v-for="account in filteredAccounts"
									:key="account.id"
									class="cursor-pointer p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
									:class="{ 'bg-blue-50 dark:bg-blue-900/20': selectedAccount?.id === account.id }"
									@click="selectAccount(account)"
								>
									<div class="flex items-center gap-2">
										<span class="fa-light fa-user text-base"></span>
										<span>{{ account.name }}</span>
									</div>
								</div>
							</div>
						</div>
					</template>

					<template v-if="activeFilter.name === 'duration'">
						<div class="space-y-6">
							<div>
								<h4 class="mb-2 font-medium text-gray-900 dark:text-white">Call Duration (seconds)</h4>
								<div class="flex gap-4">
									<div class="flex-1">
										<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Min</label>
										<input
											v-model="durationMin"
											type="number"
											min="0"
											class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
											@change="validateAndUpdateDuration"
										/>
									</div>
									<div class="flex-1">
										<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Max</label>
										<input
											v-model="durationMax"
											type="number"
											min="0"
											class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
											@change="validateAndUpdateDuration"
										/>
									</div>
								</div>
							</div>
							<div>
								<h4 class="mb-2 font-medium text-gray-900 dark:text-white">
									Billable Duration (seconds)
								</h4>
								<div class="flex gap-4">
									<div class="flex-1">
										<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Min</label>
										<input
											v-model="billableDurationMin"
											type="number"
											min="0"
											class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
											@change="validateAndUpdateDuration"
										/>
									</div>
									<div class="flex-1">
										<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Max</label>
										<input
											v-model="billableDurationMax"
											type="number"
											min="0"
											class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
											@change="validateAndUpdateDuration"
										/>
									</div>
								</div>
							</div>
						</div>
					</template>

					<template v-if="activeFilter.name === 'zone'">
						<div>
							<input
								v-model="zoneSearch"
								type="text"
								placeholder="Search zones..."
								class="mb-2 w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 placeholder:text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-400"
							/>
							<div
								class="max-h-60 overflow-y-auto rounded-md border border-gray-200 dark:border-gray-700"
							>
								<div
									v-for="zone in filteredZones"
									:key="zone"
									class="flex cursor-pointer items-center p-2 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700"
									@click="toggleZone(zone)"
								>
									<span
										class="fa-light mr-2"
										:class="selectedZones.includes(zone) ? 'fa-square-check' : 'fa-square'"
									></span>
									{{ zone }}
								</div>
							</div>
						</div>
					</template>

					<template v-if="activeFilter.name === 'call'">
						<div class="space-y-4">
							<div>
								<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Call From</label>
								<input
									v-model="callFrom"
									type="text"
									class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
									:disabled="!!callAnywhere"
									@change="updateFilterSection('call')"
								/>
							</div>
							<div>
								<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Call To</label>
								<input
									v-model="callTo"
									type="text"
									class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
									:disabled="!!callAnywhere"
									@change="updateFilterSection('call')"
								/>
							</div>
							<div>
								<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Call Anywhere</label>
								<input
									v-model="callAnywhere"
									type="text"
									class="w-full rounded-md border border-gray-200 bg-white p-2 text-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:disabled:bg-gray-900"
									:disabled="!!(callFrom || callTo)"
									@change="handleCallAnywhereChange"
								/>
							</div>
							<div class="space-y-2">
								<label class="mb-1 block text-sm text-gray-600 dark:text-gray-400">Filters</label>
								<div class="flex flex-col space-y-2">
									<div class="flex items-center">
										<input
											id="mobileWithRecording"
											v-model="withRecording"
											type="checkbox"
											class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
											@change="updateFilterSection('call')"
										/>
										<label
											for="mobileWithRecording"
											class="ml-2 text-sm text-gray-700 dark:text-gray-300"
											>With Recording</label
										>
									</div>
									<div class="flex items-center">
										<input
											id="mobileWithTranscription"
											v-model="withTranscription"
											type="checkbox"
											class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
											@change="updateFilterSection('call')"
										/>
										<label
											for="mobileWithTranscription"
											class="ml-2 text-sm text-gray-700 dark:text-gray-300"
											>With Transcription</label
										>
									</div>
									<div class="flex items-center">
										<input
											id="mobileWithVoicemail"
											v-model="withVoicemail"
											type="checkbox"
											class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700"
											@change="updateFilterSection('call')"
										/>
										<label
											for="mobileWithVoicemail"
											class="ml-2 text-sm text-gray-700 dark:text-gray-300"
											>With Voicemail</label
										>
									</div>
								</div>
							</div>
							<div>
								<label class="mb-2 block text-sm text-gray-600 dark:text-gray-400">Hangup Codes</label>
								<div class="max-h-[300px] overflow-y-auto">
									<div class="space-y-4">
										<div v-for="(codes, group) in statusCodes" :key="group" class="space-y-2">
											<div
												v-if="group !== 'Common'"
												class="font-medium text-gray-700 dark:text-gray-300"
											>
												{{ group }}
											</div>
											<div class="flex flex-wrap gap-1.5">
												<button
													v-for="code in codes"
													:key="code.code"
													class="flex items-center gap-1.5 rounded border px-2 py-1 text-xs transition-colors"
													:class="{
														'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-900/20 dark:text-blue-300':
															selectedHangupCodes.some((h) => h.code === code.code),
														'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600 dark:hover:bg-gray-700':
															!selectedHangupCodes.some((h) => h.code === code.code)
													}"
													:title="code.description"
													@click="toggleHangupCode(code.code)"
												>
													{{ code.code }}
													<span
														class="max-w-[120px] truncate text-[10px] text-gray-500 dark:text-gray-400"
													>
														{{ code.description }}
													</span>
												</button>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</template>
				</template>
			</div>

			<div class="flex justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
				<button
					class="rounded-md bg-gray-100 px-4 py-2 text-sm text-gray-900 hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
					@click="clearCurrentFilter(activeFilter.name)"
				>
					Clear
				</button>
				<button
					class="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700"
					@click="applyMobileFilter"
				>
					Apply
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import moment from 'moment'
export default {
	components: {
		Popover,
		PopoverContent,
		PopoverTrigger
	},
	inject: ['userRoles'],
	props: {
		tenants: {
			type: Array,
			default: () => []
		},
		accounts: {
			type: Array,
			default: () => []
		},
		zoneNames: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		initialDates: {
			type: Array,
			default: () => [moment().startOf('month').toDate(), null]
		},
		searchButtonText: {
			type: String,
			default: 'Search'
		},
		parentParams: {
			type: Object,
			default: () => ({})
		},
		carriers: {
			type: Array,
			default: () => []
		},
		maxDateRange: {
			type: Number,
			default: 30 // Default to 30
		}
	},

	emits: ['apply-filters', 'clear-filters', 'export'],

	data() {
		return {
			showFilterPopup: false,
			activeFilter: null,
			activeButtonRect: null,
			filterSections: [
				{ name: 'dates', label: 'Dates', hasValue: false, isOpen: false },
				{
					name: 'tenant',
					label: 'Tenant',
					hasValue: false,
					isOpen: false,
					needsTenants: true
				},
				{
					name: 'account',
					label: 'Account',
					hasValue: false,
					isOpen: false,
					needsAccounts: true
				},
				{ name: 'duration', label: 'Duration', hasValue: false, isOpen: false },
				{ name: 'zone', label: 'Zone', hasValue: false, isOpen: false },
				{ name: 'call', label: 'Call Details', hasValue: false, isOpen: false },
				{ name: 'carrier', label: 'Carrier', hasValue: false, isOpen: false }
			],
			dates: [],
			selectedTenant: '',
			accountSearch: '',
			selectedAccount: '',
			durationMin: '',
			durationMax: '',
			billableDurationMin: '',
			billableDurationMax: '',
			zoneSearch: '',
			selectedZones: [],
			callFrom: '',
			callTo: '',
			showMoreFiltersMenu: false,
			windowWidth: window.innerWidth,
			selectedHangupCodes: [],
			callAnywhere: '',
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: '',
				carrier: '',
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				zoneNames: [],
				hangupCodes: [],
				teleflowId: null,
				withRecording: null,
				withTranscription: null,
				withVoicemail: null
			},
			statusCodes: {
				Common: [
					{ code: 200, description: 'OK' },
					{ code: 301, description: 'Moved Permanently' },
					{ code: 302, description: 'Moved Temporarily' },
					{ code: 400, description: 'Bad Request' },
					{ code: 401, description: 'Unauthorized' },
					{ code: 403, description: 'Forbidden' },
					{ code: 404, description: 'Not Found' },
					{ code: 407, description: 'Proxy Authentication Required' },
					{ code: 408, description: 'Request Timeout' },
					{ code: 480, description: 'Temporarily Unavailable' },
					{ code: 481, description: 'Call/Transaction Does Not Exist' },
					{ code: 484, description: 'Address Incomplete' },
					{ code: 486, description: 'Busy Here' },
					{ code: 487, description: 'Request Terminated' },
					{ code: 488, description: 'Not Acceptable Here' },
					{ code: 500, description: 'Internal Server Error' },
					{ code: 502, description: 'Bad Gateway' },
					{ code: 503, description: 'Service Unavailable' },
					{ code: 603, description: 'Decline' }
				],
				'2xx': [
					{ code: 200, description: 'OK' },
					{ code: 202, description: 'Accepted' },
					{ code: 204, description: 'No Notification' }
				],
				'3xx': [
					{ code: 300, description: 'Multiple Choices' },
					{ code: 301, description: 'Moved Permanently' },
					{ code: 302, description: 'Moved Temporarily' },
					{ code: 305, description: 'Use Proxy' },
					{ code: 380, description: 'Alternative Service' }
				],
				'4xx': [
					{ code: 400, description: 'Bad Request' },
					{ code: 401, description: 'Unauthorized' },
					{ code: 402, description: 'Payment Required' },
					{ code: 403, description: 'Forbidden' },
					{ code: 404, description: 'Not Found' },
					{ code: 405, description: 'Method Not Allowed' },
					{ code: 406, description: 'Not Acceptable' },
					{ code: 407, description: 'Proxy Authentication Required' },
					{ code: 408, description: 'Request Timeout' },
					{ code: 409, description: 'Conflict' },
					{ code: 410, description: 'Gone' },
					{ code: 411, description: 'Length Required' },
					{ code: 412, description: 'Conditional Request Failed' },
					{ code: 413, description: 'Request Entity Too Large' },
					{ code: 414, description: 'Request-URI Too Long' },
					{ code: 415, description: 'Unsupported Media Type' },
					{ code: 416, description: 'Unsupported URI Scheme' },
					{ code: 417, description: 'Unknown Resource-Priority' },
					{ code: 420, description: 'Bad Extension' },
					{ code: 421, description: 'Extension Required' },
					{ code: 422, description: 'Session Interval Too Small' },
					{ code: 423, description: 'Interval Too Brief' },
					{ code: 424, description: 'Bad Location Information' },
					{ code: 425, description: 'Bad Alert Message' },
					{ code: 428, description: 'Use Identity Header' },
					{ code: 429, description: 'Provide Referrer Identity' },
					{ code: 430, description: 'Flow Failed' },
					{ code: 433, description: 'Anonymity Disallowed' },
					{ code: 436, description: 'Bad Identity-Info' },
					{ code: 437, description: 'Unsupported Certificate' },
					{ code: 438, description: 'Invalid Identity Header' },
					{ code: 439, description: 'First Hop Lacks Outbound Support' },
					{ code: 440, description: 'Max-Breadth Exceeded' },
					{ code: 469, description: 'Bad Info Package' },
					{ code: 470, description: 'Consent Needed' },
					{ code: 480, description: 'Temporarily Unavailable' },
					{ code: 481, description: 'Call/Transaction Does Not Exist' },
					{ code: 482, description: 'Loop Detected' },
					{ code: 483, description: 'Too Many Hops' },
					{ code: 484, description: 'Address Incomplete' },
					{ code: 485, description: 'Ambiguous' },
					{ code: 486, description: 'Busy Here' },
					{ code: 487, description: 'Request Terminated' },
					{ code: 488, description: 'Not Acceptable Here' },
					{ code: 489, description: 'Bad Event' },
					{ code: 491, description: 'Request Pending' },
					{ code: 493, description: 'Undecipherable' },
					{ code: 494, description: 'Security Agreement Required' }
				],
				'5xx': [
					{ code: 500, description: 'Internal Server Error' },
					{ code: 501, description: 'Not Implemented' },
					{ code: 502, description: 'Bad Gateway' },
					{ code: 503, description: 'Service Unavailable' },
					{ code: 504, description: 'Server Time-out' },
					{ code: 505, description: 'Version Not Supported' },
					{ code: 513, description: 'Message Too Large' },
					{ code: 555, description: 'Push Notification Service Not Supported' },
					{ code: 580, description: 'Precondition Failure' }
				],
				'6xx': [
					{ code: 600, description: 'Busy Everywhere' },
					{ code: 603, description: 'Decline' },
					{ code: 604, description: 'Does Not Exist Anywhere' },
					{ code: 606, description: 'Not Acceptable' },
					{ code: 607, description: 'Unwanted' },
					{ code: 608, description: 'Rejected' }
				]
			},
			tenantSearch: '',
			carrierSearch: '',
			selectedCarrier: null,
			withRecording: null,
			withTranscription: null,
			withVoicemail: null
		}
	},

	computed: {
		isMobile() {
			return this.windowWidth < 640
		},

		calendarCount() {
			return this.isMobile ? 1 : 2
		},

		sortedTenants() {
			return [...this.tenants].sort((a, b) =>
				a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
			)
		},

		filteredAccounts() {
			if (!this.accounts?.length) return []

			let accounts = this.accounts
			if (this.selectedTenant) {
				accounts = accounts.filter((account) => account.tenant?.id === this.selectedTenant.id)
			}
			if (!this.accountSearch) {
				return [...accounts].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
			}
			const search = this.accountSearch.toLowerCase()
			return [...accounts]
				.filter((account) => account.name.toLowerCase().includes(search))
				.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		},

		filteredZones() {
			if (!this.zoneSearch) return this.zoneNames
			const search = this.zoneSearch.toLowerCase()
			return this.zoneNames.filter((zone) => zone.toLowerCase().includes(search))
		},

		visibleFilters() {
			return this.filterSections.filter((filter) => {
				if (filter.name === 'carrier' && this.$global.hasUserRole(this.userRoles, 'account')) {
					return false
				}
				return true
			})
		},

		hiddenFilters() {
			return []
		},

		showMoreButton() {
			return false
		},

		popoverStyle() {
			if (window.innerWidth < 640) return {}

			if (!this.activeButtonRect)
				return {
					left: '0',
					right: '0',
					top: 'calc(100% + 0.5rem)'
				}

			const { left, width, bottom } = this.activeButtonRect
			const containerRect = this.$el.getBoundingClientRect()
			const relativeLeft = left - containerRect.left

			return {
				left: `${relativeLeft}px`,
				top: `${bottom - containerRect.top + 8}px`,
				minWidth: `${Math.max(width * 2, 320)}px`
			}
		},

		hasActiveFilters() {
			return (
				this.dates?.length ||
				this.selectedTenant ||
				this.selectedAccount ||
				this.selectedCarrier ||
				this.durationMin ||
				this.durationMax ||
				this.billableDurationMin ||
				this.billableDurationMax ||
				this.selectedZones.length ||
				this.callFrom ||
				this.callTo ||
				this.callAnywhere ||
				this.selectedHangupCodes.length
			)
		},

		filteredTenants() {
			if (!this.tenants?.length) return []

			if (!this.tenantSearch) {
				return [...this.tenants].sort((a, b) =>
					a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
				)
			}

			const search = this.tenantSearch.toLowerCase()
			return [...this.tenants]
				.filter((tenant) => tenant.companyName.toLowerCase().includes(search))
				.sort((a, b) => a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase()))
		},

		filteredCarriers() {
			if (!this.carriers?.length) return []

			let filteredCarriers = this.carriers
			if (this.selectedTenant) {
				filteredCarriers = filteredCarriers.filter(
					(carrier) => !carrier.tenant || carrier.tenant.id === this.selectedTenant.id
				)
			}

			if (this.carrierSearch) {
				const search = this.carrierSearch.toLowerCase()
				filteredCarriers = filteredCarriers.filter((carrier) => carrier.name.toLowerCase().includes(search))
			}

			return [...filteredCarriers].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
		}
	},

	watch: {
		initialDates: {
			handler(newDates) {
				if (newDates?.length) {
					this.dates = newDates
					this.updateFilterSection('dates')
					const filter = this.filterSections.find((f) => f.name === 'dates')
					if (filter) {
						filter.hasValue = true
					}
				}
			},
			immediate: true
		},

		parentParams: {
			handler(newParams) {
				if (newParams) {
					this.initialiseFromParams(newParams)
				}
			},
			immediate: true,
			deep: true
		}
	},

	created() {
		if (this.initialDates?.length) {
			this.dates = this.initialDates
			this.updateFilterSection('dates')
		}
	},

	mounted() {
		document.addEventListener('keydown', this.handleEscKey)
		document.addEventListener('click', this.handleClickOutside)
		window.addEventListener('resize', this.handleResize)
		this.handleResize()
	},

	beforeUnmount() {
		document.removeEventListener('keydown', this.handleEscKey)
		document.removeEventListener('click', this.handleClickOutside)
		window.removeEventListener('resize', this.handleResize)
	},

	methods: {
		handleResize() {
			this.windowWidth = window.innerWidth
		},

		handleButtonHover(filterName, event) {
			if (window.innerWidth >= 640) {
				this.activeButtonRect = event.target.getBoundingClientRect()
			}
		},

		toggleFilter(filterName) {
			if (this.activeFilter === filterName) {
				this.closeFilterPopup()
			} else {
				this.activeFilter = filterName
				this.showFilterPopup = true
			}
		},

		closeFilterPopup() {
			this.showFilterPopup = false
			this.activeFilter = null
			this.activeButtonRect = null
		},

		toggleZone(zone) {
			const index = this.selectedZones.indexOf(zone)
			if (index >= 0) {
				this.selectedZones.splice(index, 1)
			} else {
				this.selectedZones.push(zone)
			}
		},

		selectTenant(tenant) {
			this.selectedTenant = tenant

			const tenantFilter = this.filterSections.find((f) => f.name === 'tenant')
			if (tenantFilter) {
				tenantFilter.hasValue = !!tenant
			}

			this.selectedAccount = null
			this.accountSearch = ''

			this.selectedCarrier = null
			this.carrierSearch = ''

			const accountFilter = this.filterSections.find((f) => f.name === 'account')
			if (accountFilter) {
				accountFilter.hasValue = false
			}

			const carrierFilter = this.filterSections.find((f) => f.name === 'carrier')
			if (carrierFilter) {
				carrierFilter.hasValue = false
			}

			const filters = {
				...this.searchParams,
				selectedTenant: tenant,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				zoneNames: this.selectedZones || [],
				hangupCodes:
					this.selectedHangupCodes?.map((code) => (typeof code === 'object' ? code.code : code)) || []
			}

			this.searchParams = filters

			this.updateFilterSection('tenant', true, false)
		},

		selectAccount(account) {
			this.selectedAccount = account

			const accountFilter = this.filterSections.find((f) => f.name === 'account')
			if (accountFilter) {
				accountFilter.hasValue = !!account
			}

			const filters = {
				dateAddedFrom: this.dates[0] ? new Date(this.dates[0]).toISOString().split('T')[0] : '',
				dateAddedTo: this.dates[1] ? new Date(this.dates[1]).toISOString().split('T')[0] : '',
				selectedTenant: this.selectedTenant,
				selectedAccount: account,
				carrier: this.selectedCarrier ? this.selectedCarrier.id : null,
				carrierName: this.selectedCarrier ? this.selectedCarrier.name : null,
				callDurationFrom: this.durationMin ? parseInt(this.durationMin) : null,
				callDurationTo: this.durationMax ? parseInt(this.durationMax) : null,
				billableDurationFrom: this.billableDurationMin ? parseInt(this.billableDurationMin) : null,
				billableDurationTo: this.billableDurationMax ? parseInt(this.billableDurationMax) : null,
				callFrom: this.callFrom || '',
				callTo: this.callTo || '',
				callAnywhere: this.callAnywhere || '',
				zoneNames: this.selectedZones || [],
				hangupCodes: this.selectedHangupCodes.map((code) => (typeof code === 'object' ? code.code : code)),
				isSearching: false
			}

			this.$emit('apply-filters', filters, account)

			this.searchParams = filters
		},

		updateFilterSection(section, shouldEmit = true, isSearching = false) {
			this.filterSections.forEach((filter) => {
				if (filter.name === section) {
					switch (section) {
						case 'dates':
							filter.hasValue = this.dates?.length > 0
							break
						case 'tenant':
							filter.hasValue = !!this.selectedTenant
							break
						case 'account':
							filter.hasValue = !!this.selectedAccount
							break
						case 'carrier':
							filter.hasValue = !!this.selectedCarrier
							break
						case 'duration':
							filter.hasValue = !!(
								this.durationMin ||
								this.durationMax ||
								this.billableDurationMin ||
								this.billableDurationMax
							)
							break
						case 'zone':
							filter.hasValue = this.selectedZones?.length > 0
							break
						case 'call':
							filter.hasValue = !!(
								this.callFrom ||
								this.callTo ||
								this.callAnywhere ||
								this.withRecording ||
								this.withTranscription ||
								this.withVoicemail ||
								(this.selectedHangupCodes && this.selectedHangupCodes.length > 0)
							)
							break
					}
				}
			})

			if (shouldEmit) {
				const filters = {
					dateAddedFrom: this.dates[0] ? new Date(this.dates[0]).toISOString().split('T')[0] : null,
					dateAddedTo: this.dates[1] ? new Date(this.dates[1]).toISOString().split('T')[0] : null,
					selectedTenant: this.selectedTenant,
					selectedAccount: this.selectedAccount,
					carrier: this.selectedCarrier ? { id: this.selectedCarrier.id } : null,
					carrierName: this.selectedCarrier ? this.selectedCarrier.name : null,
					callDurationFrom: this.durationMin ? parseInt(this.durationMin) : null,
					callDurationTo: this.durationMax ? parseInt(this.durationMax) : null,
					billableDurationFrom: this.billableDurationMin ? parseInt(this.billableDurationMin) : null,
					billableDurationTo: this.billableDurationMax ? parseInt(this.billableDurationMax) : null,
					callFrom: this.callFrom || '',
					callTo: this.callTo || '',
					callAnywhere: this.callAnywhere || '',
					zoneNames: this.selectedZones || [],
					hangupCodes: this.selectedHangupCodes.map((code) => (typeof code === 'object' ? code.code : code)),
					withRecording: this.withRecording || null,
					withTranscription: this.withTranscription || null,
					withVoicemail: this.withVoicemail || null,
					isSearching
				}

				this.searchParams = { ...this.searchParams, ...filters }

				this.$emit('apply-filters', filters, this.selectedAccount)
			}
		},

		applyFilters(filter, isSearching = false) {
			if (filter) {
				this.updateFilterSection(filter.name, true, isSearching)

				this.closePopover(filter)
			} else {
				const filters = {
					dateAddedFrom: this.dates[0] ? new Date(this.dates[0]).toISOString().split('T')[0] : null,
					dateAddedTo: this.dates[1] ? new Date(this.dates[1]).toISOString().split('T')[0] : null,
					selectedTenant: this.selectedTenant,
					selectedAccount: this.selectedAccount,
					carrier: this.selectedCarrier ? { id: this.selectedCarrier.id } : null,
					carrierName: this.selectedCarrier ? this.selectedCarrier.name : null,
					callDurationFrom: this.durationMin ? parseInt(this.durationMin) : null,
					callDurationTo: this.durationMax ? parseInt(this.durationMax) : null,
					billableDurationFrom: this.billableDurationMin ? parseInt(this.billableDurationMin) : null,
					billableDurationTo: this.billableDurationMax ? parseInt(this.billableDurationMax) : null,
					callFrom: this.callFrom || '',
					callTo: this.callTo || '',
					callAnywhere: this.callAnywhere || '',
					zoneNames: this.selectedZones || [],
					hangupCodes: this.selectedHangupCodes.map((code) => (typeof code === 'object' ? code.code : code)),
					withRecording: this.withRecording || null,
					withTranscription: this.withTranscription || null,
					withVoicemail: this.withVoicemail || null,
					isSearching: true
				}

				this.filterSections.forEach((section) => {
					this.updateFilterSection(section.name, false)
				})

				this.$emit('apply-filters', filters, this.selectedAccount)

				this.filterSections.forEach((section) => {
					section.isOpen = false
				})
			}
		},

		clearFilters() {
			this.dates = this.initialDates
			this.selectedCarrier = null
			this.selectedTenant = null
			this.selectedAccount = null
			this.callFrom = ''
			this.callTo = ''
			this.callAnywhere = ''
			this.selectedHangupCodes = []
			this.selectedZones = []
			this.durationMin = ''
			this.durationMax = ''
			this.billableDurationMin = ''
			this.billableDurationMax = ''
			this.accountSearch = ''
			this.tenantSearch = ''
			this.zoneSearch = ''

			this.filterSections.forEach((filter) => {
				filter.hasValue = false
				filter.isOpen = false
			})

			this.searchParams = {
				dateAddedFrom: '',
				dateAddedTo: '',
				selectedTenant: null,
				selectedAccount: null,
				carrier: null,
				carrierName: null,
				callDurationFrom: null,
				callDurationTo: null,
				billableDurationFrom: null,
				billableDurationTo: null,
				callFrom: '',
				callTo: '',
				callAnywhere: '',
				zoneNames: [],
				hangupCodes: []
			}

			this.$emit('clear-filters')
		},

		toggleMoreFilters() {
			this.showMoreFiltersMenu = !this.showMoreFiltersMenu
		},

		selectHiddenFilter(filter) {
			this.activeFilter = filter.name
			this.showFilterPopup = true
		},

		getFilterIcon(name) {
			const icons = {
				dates: 'fa-calendar',
				tenant: 'fa-users',
				account: 'fa-user',
				duration: 'fa-clock',
				zone: 'fa-map',
				call: 'fa-phone',
				carrier: 'fa-satellite-dish'
			}
			return icons[name] || 'fa-filter'
		},

		handleEscKey(event) {
			if (event.key === 'Escape' && this.showFilterPopup) {
				this.closeFilterPopup()
			}
		},

		handleClickOutside(event) {
			if (this.showFilterPopup && this.$refs.filterPopup && !this.$el.contains(event.target)) {
				this.closeFilterPopup()
			}
		},

		clearCurrentFilter(filterName) {
			let accountFilter

			switch (filterName) {
				case 'dates':
					this.dates = []
					break
				case 'tenant':
					this.selectedTenant = null

					this.selectedAccount = null
					this.accountSearch = ''
					accountFilter = this.filterSections.find((f) => f.name === 'account')
					if (accountFilter) {
						accountFilter.hasValue = false
					}
					break
				case 'account':
					this.selectedAccount = null
					this.accountSearch = ''
					break
				case 'carrier':
					this.selectedCarrier = null
					this.carrierSearch = ''

					this.searchParams.carrier = null
					this.searchParams.carrierName = null
					break
				case 'duration':
					this.durationMin = ''
					this.durationMax = ''
					this.billableDurationMin = ''
					this.billableDurationMax = ''
					break
				case 'zone':
					this.selectedZones = []
					this.zoneSearch = ''
					break
				case 'call':
					this.callFrom = ''
					this.callTo = ''
					this.callAnywhere = ''
					this.selectedHangupCodes = []
					this.withRecording = null
					this.withTranscription = null
					this.withVoicemail = null
					break
			}

			const filter = this.filterSections.find((f) => f.name === filterName)
			if (filter) {
				filter.hasValue = false
				filter.isOpen = false
			}

			this.updateFilterSection(filterName, true, false)
		},

		setAdvancedParams(isSearching = false) {
			const filters = {
				dateAddedFrom: this.dates[0] ? new Date(this.dates[0]).toISOString().split('T')[0] : '',
				dateAddedTo: this.dates[1] ? new Date(this.dates[1]).toISOString().split('T')[0] : '',
				selectedTenant: this.selectedTenant,
				selectedAccount: this.selectedAccount || '',
				carrier: this.selectedCarrier ? { id: this.selectedCarrier.id } : null,
				carrierName: this.selectedCarrier ? this.selectedCarrier.name : null,
				callFrom: this.callFrom || '',
				callTo: this.callTo || '',
				callAnywhere: this.callAnywhere || '',
				zoneNames: this.selectedZones || [],
				hangupCodes: this.selectedHangupCodes.map((code) => (typeof code === 'object' ? code.code : code)),
				isSearching
			}

			this.filterSections.forEach((section) => {
				this.updateFilterSection(section.name, false)
			})

			this.$emit('apply-filters', filters)
		},

		getFilterTitle(name) {
			const titles = {
				tenant: 'Select Tenant',
				account: 'Select Account',
				duration: 'Call Duration',
				zone: 'Tariff Zones',
				call: 'Call Details',
				dates: 'Select Date Range',
				carrier: 'Select Carrier'
			}
			return titles[name] || name
		},

		closePopover(filter) {
			if (this.isMobile) {
				this.closeMobileFilter()
			} else if (filter) {
				filter.isOpen = false
			}
		},

		toggleHangupCode(code) {
			const index = this.selectedHangupCodes.findIndex((h) => h.code === code)
			if (index >= 0) {
				this.selectedHangupCodes.splice(index, 1)
			} else {
				let codeDetails
				for (const group in this.statusCodes) {
					const found = this.statusCodes[group].find((h) => h.code === code)
					if (found) {
						codeDetails = found
						break
					}
				}
				if (codeDetails) {
					this.selectedHangupCodes.push(codeDetails)
				}
			}
			this.updateFilterSection('call')
		},

		openMobileFilter(filter) {
			this.activeFilter = filter
			filter.isOpen = true
			document.body.style.overflow = 'hidden'
		},

		closeMobileFilter() {
			if (this.activeFilter) {
				this.activeFilter.isOpen = false
				this.activeFilter = null
				document.body.style.overflow = ''
			}
		},

		applyMobileFilter() {
			if (this.activeFilter) {
				this.applyFilters(this.activeFilter, true)

				this.closeMobileFilter()
			}
		},

		validateAndUpdateDuration() {
			let min = this.durationMin === '' ? null : parseInt(this.durationMin) || 0
			let max = this.durationMax === '' ? null : parseInt(this.durationMax) || 0
			let billMin = this.billableDurationMin === '' ? null : parseInt(this.billableDurationMin) || 0
			let billMax = this.billableDurationMax === '' ? null : parseInt(this.billableDurationMax) || 0

			if (min !== null) min = Math.max(0, min)
			if (max !== null) max = Math.max(0, max)
			if (billMin !== null) billMin = Math.max(0, billMin)
			if (billMax !== null) billMax = Math.max(0, billMax)

			if (max !== null && min !== null && min > max) {
				max = min
			}

			if (billMax !== null && billMin !== null && billMin > billMax) {
				billMax = billMin
			}

			this.durationMin = min !== null ? min.toString() : ''
			this.durationMax = max !== null ? max.toString() : ''
			this.billableDurationMin = billMin !== null ? billMin.toString() : ''
			this.billableDurationMax = billMax !== null ? billMax.toString() : ''

			this.updateFilterSection('duration')
		},

		handleCallAnywhereChange() {
			if (this.callAnywhere) {
				this.callFrom = ''
				this.callTo = ''
			}

			this.searchParams.callAnywhere = this.callAnywhere
			this.searchParams.callFrom = this.callFrom
			this.searchParams.callTo = this.callTo

			this.updateFilterSection('call', true, false)
		},

		initialiseFromParams(params) {
			this.dates = []
			this.selectedTenant = null
			this.selectedAccount = null
			this.durationMin = ''
			this.durationMax = ''
			this.billableDurationMin = ''
			this.billableDurationMax = ''
			this.callFrom = ''
			this.callTo = ''
			this.callAnywhere = ''
			this.selectedZones = []
			this.selectedHangupCodes = []
			this.tenantSearch = ''
			this.accountSearch = ''
			this.zoneSearch = ''
			this.carrierSearch = ''
			this.selectedCarrier = null
			this.withRecording = null
			this.withTranscription = null
			this.withVoicemail = null

			if (params.dateAddedFrom || params.dateAddedTo) {
				this.dates = [
					params.dateAddedFrom ? new Date(params.dateAddedFrom) : null,
					params.dateAddedTo ? new Date(params.dateAddedTo) : null
				].filter(Boolean)
			}

			if (params.selectedTenant) {
				this.selectedTenant = params.selectedTenant
			}

			if (params.selectedAccount) {
				this.selectedAccount = params.selectedAccount
			}

			if (params.carrier) {
				const carrierId = typeof params.carrier === 'object' ? params.carrier.id : params.carrier

				const carrier = this.carriers.find((c) => {
					return (
						c.id.toString() === carrierId.toString() &&
						(!params.selectedTenant || !c.tenant || c.tenant.id === params.selectedTenant.id)
					)
				})

				this.selectedCarrier = carrier || null
			}

			if (params.callDurationFrom !== null && params.callDurationFrom !== undefined) {
				this.durationMin = params.callDurationFrom?.toString() || ''
			}

			if (params.callDurationTo !== null && params.callDurationTo !== undefined) {
				this.durationMax = params.callDurationTo?.toString() || ''
			}

			if (params.billableDurationFrom !== null && params.billableDurationFrom !== undefined) {
				this.billableDurationMin = params.billableDurationFrom?.toString() || ''
			}

			if (params.billableDurationTo !== null && params.billableDurationTo !== undefined) {
				this.billableDurationMax = params.billableDurationTo?.toString() || ''
			}

			if (params.callFrom || params.callTo || params.callAnywhere) {
				this.callFrom = params.callFrom || ''
				this.callTo = params.callTo || ''
				this.callAnywhere = params.callAnywhere || ''
			}

			if (params.zoneNames?.length) {
				this.selectedZones = [...params.zoneNames]
			}

			if (params.hangupCodes?.length) {
				this.selectedHangupCodes = params.hangupCodes.map((code) => {
					for (const group in this.statusCodes) {
						const found = this.statusCodes[group].find((h) => h.code === code)
						if (found) return found
					}
					return { code, description: '' }
				})
			}

			this.withRecording = params.withRecording === true
			this.withTranscription = params.withTranscription === true
			this.withVoicemail = params.withVoicemail === true

			this.filterSections.forEach((section) => {
				this.updateFilterSection(section.name, false)
			})

			this.searchParams = { ...this.searchParams, ...params }
		},

		selectCarrier(carrier) {
			this.selectedCarrier = carrier

			const carrierFilter = this.filterSections.find((f) => f.name === 'carrier')
			if (carrierFilter) {
				carrierFilter.hasValue = !!carrier
			}

			const filters = {
				...this.searchParams,
				carrier: carrier ? { id: carrier.id } : null,
				carrierName: carrier ? carrier.name : null
			}

			this.searchParams = filters

			this.updateFilterSection('carrier', true, false)
		},

		setPresetDate(preset) {
			const now = new Date()
			let start, end
			let presetParam = ''

			if (preset === '24h') {
				end = now
				start = new Date(now.getTime() - 24 * 60 * 60 * 1000)
				presetParam = 'last24h'
			} else if (preset === '7d') {
				end = now
				start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
				presetParam = 'lastWeek'
			} else if (preset === '30d') {
				end = now
				start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
				presetParam = 'last30d'
			} else if (preset === 'thisMonth') {
				start = new Date(now.getFullYear(), now.getMonth(), 1, 5, 0, 0, 0)
				end = now
				presetParam = 'thisMonth'
			} else if (preset === 'lastMonth') {
				start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 5, 0, 0, 0)
				end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
				presetParam = 'lastMonth'
			}

			if (start && end) {
				if ((end - start) / (1000 * 60 * 60 * 24) > this.maxDateRange) {
					start = new Date(end.getTime() - this.maxDateRange * 24 * 60 * 60 * 1000)
				}
				this.dates = [start, end]

				const filters = {
					dateAddedFrom: start.toISOString().split('T')[0],
					dateAddedTo: end.toISOString().split('T')[0],
					selectedTenant: this.selectedTenant,
					selectedAccount: this.selectedAccount,
					carrier: this.selectedCarrier ? { id: this.selectedCarrier.id } : null,
					carrierName: this.selectedCarrier ? this.selectedCarrier.name : null,
					callDurationFrom: this.durationMin ? parseInt(this.durationMin) : null,
					callDurationTo: this.durationMax ? parseInt(this.durationMax) : null,
					billableDurationFrom: this.billableDurationMin ? parseInt(this.billableDurationMin) : null,
					billableDurationTo: this.billableDurationMax ? parseInt(this.billableDurationMax) : null,
					callFrom: this.callFrom || '',
					callTo: this.callTo || '',
					callAnywhere: this.callAnywhere || '',
					zoneNames: this.selectedZones || [],
					hangupCodes: this.selectedHangupCodes.map((code) => (typeof code === 'object' ? code.code : code)),
					withRecording: this.withRecording || null,
					withTranscription: this.withTranscription || null,
					withVoicemail: this.withVoicemail || null,
					preset: presetParam,
					isSearching: false
				}

				this.$emit('apply-filters', filters)
				this.updateFilterSection('dates')
			}
		},

		handleDateChange(val) {
			if (val && val.length === 2) {
				const start = new Date(val[0])
				const end = new Date(val[1])
				const diff = (end - start) / (1000 * 60 * 60 * 24)
				if (diff > this.maxDateRange) {
					this.dates = [start, new Date(start.getTime() + this.maxDateRange * 24 * 60 * 60 * 1000)]
				} else {
					this.dates = [start, end]
				}
				this.updateFilterSection('dates')
			}
		}
	}
}
</script>

<style scoped>
:deep(.date-picker-custom) {
	@apply w-full overflow-hidden;
}

:deep(.dp__theme_dark) {
	@apply bg-transparent;
}

:deep(.dp__menu) {
	@apply w-full min-w-0 border-none p-0 shadow-none;
}

:deep(.dp__calendar_wrap) {
	@apply flex flex-row gap-4;
	width: 100%;
	max-width: 900px;
	margin: 0 auto;
	padding: 1rem;
}

:deep(.dp__calendar) {
	@apply min-w-0 p-4;
}

:deep(.dp__calendar_header) {
	@apply text-base font-medium text-gray-700 dark:text-white;
}

:deep(.dp__calendar_item) {
	@apply text-base text-gray-600 transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700;
}

:deep(.dp__today) {
	@apply border border-blue-100 bg-blue-50/50 dark:border-blue-500/30 dark:bg-transparent;
}

:deep(.dp__active_date) {
	@apply bg-blue-500/90 text-white transition-colors hover:bg-blue-500;
}

:deep(.dp__range_between) {
	@apply bg-blue-50/70 text-blue-700 transition-colors dark:bg-blue-600/10 dark:text-blue-100;
}

:deep(.dp__range_start),
:deep(.dp__range_end) {
	@apply bg-blue-500/90 text-white transition-colors hover:bg-blue-500;
}

:deep(.dp__month_year_row) {
	@apply text-gray-700 dark:text-white;
}

:deep(.dp__month_year_select) {
	@apply transition-colors hover:bg-gray-50 dark:hover:bg-gray-700;
}

:deep(.dp__overlay_cell_active) {
	@apply bg-blue-500 text-white transition-colors hover:bg-blue-600;
}

:deep(.dp__arrow_bottom) {
	@apply border-t-gray-700 transition-colors dark:border-t-white;
}

:deep(.dp__inner_nav) {
	@apply text-gray-700 transition-colors hover:text-gray-900 dark:text-white dark:hover:text-gray-200;
}

:deep(.dp__overlay) {
	@apply bg-white text-gray-700 dark:bg-gray-800 dark:text-white;
}

:deep(.dp__btn) {
	@apply text-gray-700 transition-colors hover:text-gray-900 dark:text-white dark:hover:text-gray-200;
}

:deep(.dp__month_year_wrap) {
	@apply mb-2;
}

:deep(.dp__month_year_select) {
	@apply text-sm;
}

:deep(.dp__cell_inner) {
	@apply h-8 w-8 transition-all;
}

:deep(.dp__calendar_header_item) {
	@apply h-8 w-8;
}

:deep(.dp__calendar_row) {
	@apply h-8;
}

:deep(.dp__arrow_top) {
	@apply border-b-gray-700 transition-colors dark:border-b-white;
}

:deep(.dp__instance_calendar) {
	@apply w-full;
}

:deep(.dp__arrow_nav) {
	@apply text-gray-700 transition-colors hover:text-gray-900 dark:text-white dark:hover:text-gray-200;
}

:deep(.dp__disabled) {
	@apply text-gray-300 dark:text-gray-600;
}

:deep(.dp__weekday) {
	@apply text-gray-400 dark:text-gray-400;
}

@media (max-width: 640px) {
	:deep(.dp__calendar_wrap) {
		@apply flex-col p-0;
		min-height: 0;
		width: 100%;
		max-width: 100%;
		margin: 0;
	}

	:deep(.dp__calendar) {
		@apply w-full p-2;
		min-height: 0;
		max-width: 100%;
	}

	:deep(.PopoverContent) {
		max-width: 90vw;
		width: 90vw;
	}

	:deep(.dp__calendar_header),
	:deep(.dp__calendar_item) {
		@apply text-xs;
	}

	:deep(.dp__cell_inner) {
		@apply h-8 w-8 border-accent p-0;
		min-height: 0;
	}

	:deep(.dp__calendar_header_item) {
		@apply h-8 w-8 p-0;
		min-height: 0;
	}

	:deep(.dp__calendar_row) {
		@apply h-8;
		min-height: 0;
		display: grid;
		grid-template-columns: repeat(7, 1fr);
	}

	:deep(.dp__month_year_row) {
		@apply py-1 text-sm;
	}

	:deep(.dp__month_year_wrap) {
		@apply mb-1;
	}

	:deep(.dp__instance_calendar) {
		@apply max-w-full;
		min-height: 0;
	}

	:deep(.dp__calendar_header) {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
	}

	:deep(.dp__calendar_header_separator) {
		@apply hidden;
	}

	:deep(.dp__calendar_item) {
		@apply p-0;
	}
}

.no-scrollbar::-webkit-scrollbar {
	display: none;
}
.no-scrollbar {
	-ms-overflow-style: none;
	scrollbar-width: none;
}
</style>
